import React, { useCallback, useEffect, useRef, useState } from 'react';

// helpers
import useFetch from 'hooks/useFetch';
import useTranslation from 'hooks/useTranslation';
import useInfinityScroll from 'hooks/useInfinityScroll';
import { styled } from 'styled-components';
import { StateModel } from 'redux/reducers';
import { RoutePaths } from 'routes/routes';
import { useNavigate } from 'react-router-dom';
import { selectIntakeForm } from 'redux/actions/intakeForm';
import { FetchResponseModel } from 'typings/common';
import { DEFAULT_TABLE_LIMIT } from 'constants/global';
import { useDispatch, useSelector } from 'react-redux';
import {
  intakeFormAPI,
  IntakeSubmissionFromQuery,
} from 'api/crm/intakeFormAPI';

// components
import Application from './Application';
import LoadingWrapper from 'components/WrapperComponents/LoadingWrapper';
import NoDataContainer from 'components/Additional/NoDataContainer';
import { Col, Message, Modal, Row, Spin } from '@ui';

const Applications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeIntakeFormId = useSelector<StateModel, string | null>(
    (state) => state.intakeForm.intakeForm?._id || null,
  );
  const { t } = useTranslation('connections');
  const requestContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState<
    FetchResponseModel<IntakeSubmissionFromQuery>
  >({
    total: 0,
    data: [],
  });

  const { response, loading } = useFetch(
    () =>
      page === 1
        ? intakeFormAPI.fetchIntakeSubmissions({
            page: 1,
            limit: DEFAULT_TABLE_LIMIT,
          })
        : null,
    [page],
  );

  useEffect(() => {
    if (response && page === 1) {
      setResponseData(response);
      setHasMore(true);
    }
  }, [response, page]);

  const fetchApplications = async () => {
    if (isLoading || !hasMore || !responseData.data.length) return;
    setLoading(true);

    const response = await intakeFormAPI.fetchIntakeSubmissions({
      page: page + 1,
      limit: DEFAULT_TABLE_LIMIT,
    });

    if (responseData.data.length + response.data.length < response.total) {
      setResponseData((prevState) => ({
        ...prevState,
        data: [...prevState.data, ...response.data],
      }));
      setHasMore(true);
      setPage(page + 1);
    } else {
      setResponseData((prevState) => ({
        ...prevState,
        data: [...prevState.data, ...response.data],
      }));
      setHasMore(false);
    }
    setLoading(false);
  };

  useInfinityScroll(fetchApplications, requestContainerRef, hasMore, 'bottom');

  const selectApplication = useCallback(
    (intakeForm: IntakeSubmissionFromQuery) => {
      Modal.confirm({
        title: t('applications.switch.title'),
        width: 600,
        icon: null,
        closable: true,
        maskClosable: true,
        content: t('applications.switch.content', {
          name: intakeForm.directClientName,
        }),
        okText: t('confirm', { ns: 'common' }),
        cancelText: t('cancel', { ns: 'common' }),
        onOk() {
          dispatch(selectIntakeForm(intakeForm._id));
          Message.success(
            t('applications.switch.success_message', {
              name: intakeForm.directClientName,
            }),
          );
        },
      });
    },
    [t],
  );

  const viewApplication = (application: IntakeSubmissionFromQuery) => {
    navigate({
      pathname: RoutePaths.Connections_Application_Details,
      search: `?id=${application._id}`,
    });
  };

  return (
    <Wrapper ref={requestContainerRef}>
      <LoadingWrapper loading={loading}>
        {!responseData.data.length ? (
          <NoDataContainer noDataText={t('applications.no_data_description')} />
        ) : (
          <Row wrap={true} gutter={[16, 16]}>
            {responseData.data.map((application, index) => (
              <React.Fragment key={application._id}>
                <Col span={24}>
                  <Application
                    data={application}
                    isSelected={activeIntakeFormId == application._id}
                    onSelect={
                      application.isActive
                        ? () => selectApplication(application)
                        : undefined
                    }
                    onView={
                      !application.isActive
                        ? () => viewApplication(application)
                        : undefined
                    }
                  />
                </Col>
                {responseData.data.length < responseData.total &&
                  index === responseData.data.length - 1 && (
                    <LoaderContainer>
                      <Spin size="large" spinning={isLoading} />
                    </LoaderContainer>
                  )}
              </React.Fragment>
            ))}
          </Row>
        )}
      </LoadingWrapper>
    </Wrapper>
  );
};

const LoaderContainer = styled.div`
  height: 50px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  max-height: calc(100vh - 340px);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-top: 1px;
`;

export default Applications;
